import EditIcon from "@mui/icons-material/Edit";
import {
  Avatar,
  FormHelperText,
  Grid,
  IconButton,
  Paper,
  Stack,
  styled,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppLoader from "../../../components/apploader";
import GlobalButton from "../../../components/button";
import CustomContainer from "../../../components/container/CustomContainer";
import DashboardHeader from "../../../components/dashboardHeader";
import ImageUploader from "../../../components/imageUploader";
import InputField from "../../../components/InputField";
import Label from "../../../components/label";
import { useSnackbar } from "../../../lib/context/snackbarContext";
import { setUserData } from "../../../lib/redux/slices/userSlice";
import { baseUrl } from "../../../services";
import {
  changePasswordUser,
  updateUser,
} from "../../../services/authenticationFn";
import { getUserDetailById } from "../../../services/userDetailFn";
import { encryptPassword } from "../../../utils";
import { updateUserValidation } from "../../../utils/validation";
import AttachBankDialog from "./AttachBankDialog";
import ChangePasswordDialog from "./ChangePasswordDialog";
import SaveChangesDialog from "./SaveChangesDialog";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import { t } from "i18next";

const UserSettingPage = () => {
  const openChangePassDialog = useRef(null);
  const openSaveChangesDialog = useRef(null);
  const openAttachedAccountDialog = useRef(null);
  const naviagte = useNavigate();
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const userData = useSelector((state) => state.userReducer.user);
  const [isLoading, setIsLoading] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [inputValues, setInputValues] = useState({
    image: null,
    fname: "",
    lname: "",
    email: "",
    contact: "",
    cui: "",
    signupAs: "",
  });
  const [inputError, setInputError] = useState({
    image: false,
    fname: false,
    lname: false,
    email: false,
    isValidEmail: false,
    contact: false,
  });

  useEffect(() => {
    if (userData?._id) {
      fetchUserDetails();
    }
  }, [userData?._id]);

  const fetchUserDetails = async () => {
    try {
      setIsLoading(true);
      const response = await getUserDetailById(userData?._id);
      if (response.data.status === true) {
        const data = response.data.data;
        setUserDetails(data);
        setInputValues((prevValues) => ({
          ...prevValues,
          image: data?.profileImg || prevValues.image,
          fname: data?.firstname || prevValues.fname,
          lname: data?.lastname || prevValues.lname,
          email: data?.email || prevValues.email,
          contact: data?.contact || prevValues.contact,
          signupAs: data?.signupAs || "",
          cui: data?.cui || "",
        }));
      } else {
        showSnackbar(response.data.message, "error");
      }
    } catch (error) {
      showSnackbar(error?.message, "error");
      console.error("Error fetching user details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeInputs = (event) => {
    const { name, value } = event.target;
    setInputError((err) => ({
      ...err,
      [name]: false,
      isValidEmail: name === "email" ? false : inputError.email,
    }));
    setInputValues((val) => ({ ...val, [name]: value }));
  };

  const handleImageChange = async (event) => {
    const file = event.target.files[0];

    const maxSize = 5 * 1024 * 1024;

    if (file.size > maxSize) {
      return showSnackbar(t("imageSizeLimit"), "error");
    }

    const imageType = file.type.split("/")[1].toLowerCase();

    const allowedFormats = ["png", "jpeg", "jpg"];

    if (!allowedFormats.includes(imageType)) {
      return showSnackbar(t("imageFormatRestriction"), "error");
    }

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("image", event.target.files[0]);
      const response = await axios({
        method: "post",
        url: `${baseUrl}/image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setInputValues((prevValues) => ({
        ...prevValues,
        image: response?.data?.url,
      }));
      setInputError((prevError) => ({
        ...prevError,
        image: false,
      }));
      showSnackbar(t("imageUploadSuccess"), "success");
    } catch (error) {
      showSnackbar(t("imageUploadFailed"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateUser = async (password) => {
    try {
      const payload = {
        firstname: inputValues.fname,
        lastname: inputValues.lname,
        email: inputValues.email,
        contact: inputValues.contact,
        profileImg: inputValues.image,
        password: encryptPassword(password),
      };

      setIsLoading(true);
      const response = await updateUser(userData?._id, payload);
      if (response.data.status === "success") {
        const data = response?.data?.data;
        const updatedData = { ...userData, ...data };
        localStorage.setItem("user_data", JSON.stringify(updatedData));
        dispatch(setUserData(updatedData));
        openSaveChangesDialog.current.closeDialog();
        showSnackbar(response.data.message, "success");
      } else {
        showSnackbar(response.data.message, "error");
      }
    } catch (error) {
      showSnackbar(error?.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (params) => {
    try {
      const payload = {
        userId: userData?._id,
        newPassword: encryptPassword(params.newPassword),
        oldPassword: encryptPassword(params.oldPassword),
        role: "User",
      };

      setIsLoading(true);
      const response = await changePasswordUser(payload);
      if (response.data.status === "success") {
        showSnackbar(response.data.message, "success");
        openChangePassDialog.current.closeDialog();
      } else {
        showSnackbar(response.data.message, "error");
      }
    } catch (error) {
      showSnackbar(error?.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const renderHeadProps = () => {
    return (
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={"center"}
        spacing={{ xs: 1, sm: 2 }}
      >
        {!userDetails?.bankDetails && (
          <GlobalButton
            handleClickBtn={() =>
              openAttachedAccountDialog.current.openDialog({
                getDetails: fetchUserDetails,
                type: "add",
                data: null,
              })
            }
            btnText={"Attach Bank Account"}
            styleProps={{
              width: { xs: "100%", sm: "auto" },
            }}
          />
        )}

        <GlobalButton
          handleClickBtn={() => openChangePassDialog.current.openDialog()}
          btnText={"changePassword"}
          styleProps={{
            width: { xs: "100%", sm: "auto" },
          }}
        />
        <GlobalButton
          handleClickBtn={() => {
            const validate = updateUserValidation(inputValues, setInputError);
            if (!validate) {
              openSaveChangesDialog.current.openDialog();
            }
          }}
          btnText={"saveChanges"}
          styleProps={{
            width: { xs: "100%", sm: "auto" },
          }}
        />
      </Stack>
    );
  };

  return (
    <DashboardHeader>
      <AppLoader isVisible={isLoading} />
      <CustomContainer
        primaryHeading={t("accountDetails")}
        secondaryHeading={t("personalDetailsNote")}
        showBackButton={true}
        handleBack={() => naviagte("/dashboard")}
        paperSxProps={{
          minHeight: "80vh",
        }}
        headProps={renderHeadProps()}
      >
        <Grid container spacing={2}>
          <Grid
            item
            xs={12}
            md={3}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <Stack alignItems={"center"} mt={3}>
              <ImageUploader
                error={inputError.image}
                image={inputValues.image}
                handleImageChange={handleImageChange}
              />
              {inputError.image && (
                <FormHelperText sx={{ color: "custom.errorRed" }}>
                  {t("imageRequired")}
                </FormHelperText>
              )}
            </Stack>
          </Grid>
          <Grid item xs={12} md={9}>
            <Grid container spacing={2}>
              <Grid
                item
                xs={12}
                md={inputValues.signupAs === "individual" ? 6 : 12}
              >
                <Label
                  required
                  text={
                    inputValues.signupAs === "individual"
                      ? t("firstName")
                      : t("businessName")
                  }
                  htmlFor="user_fname"
                />
                <InputField
                  id={"user_fname"}
                  required
                  inputError={inputError.fname}
                  helperText={
                    inputError.fname &&
                    (inputValues.signupAs === "individual"
                      ? t("firstNameRequire")
                      : t("businessNameRequired"))
                  }
                  placeholder={`${t("enter")} ${
                    inputValues.signupAs === "individual"
                      ? t("firstName")
                      : t("businessName")
                  }`}
                  type="text"
                  name={"fname"}
                  value={inputValues.fname}
                  onChange={handleChangeInputs}
                />
              </Grid>

              {inputValues.signupAs === "individual" && (
                <Grid item xs={12} md={6}>
                  <Label required text={t("lastName")} htmlFor="user_lname" />
                  <InputField
                    id={"user_lname"}
                    required
                    inputError={inputError.lname}
                    helperText={inputError.lname && t("lastNameRequire")}
                    placeholder={`${t("enter")} ${t("lastName")}`}
                    type="text"
                    name={"lname"}
                    value={inputValues.lname}
                    onChange={handleChangeInputs}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Label required text={t("email")} htmlFor="user_email" />
                <InputField
                  id={"user_email"}
                  required
                  inputError={inputError.email || inputError.isValidEmail}
                  helperText={
                    inputError.email
                      ? t("emailAddRequired")
                      : inputError.isValidEmail
                      ? t("invalidEmailFormat")
                      : null
                  }
                  placeholder={`${t("enter")} ${t("emailAddress")}`}
                  type="email"
                  name={"email"}
                  value={inputValues.email}
                  onChange={handleChangeInputs}
                  readonly
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Label
                  required
                  text={t("contactNumber")}
                  htmlFor="user_contact"
                />
                <InputField
                  id={"user_contact"}
                  required
                  inputError={inputError.contact}
                  helperText={inputError.contact && t("contactRequired")}
                  placeholder={`${t("enter")} ${t("contactNumber")}`}
                  type="number"
                  name={"contact"}
                  value={inputValues.contact}
                  onChange={handleChangeInputs}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Label required text={t("signupAs")} htmlFor="user_signupAs" />
                <InputField
                  id={"user_signupAs"}
                  readonly
                  value={inputValues?.signupAs?.toUpperCase() || "N/A"}
                />
              </Grid>
              {inputValues.signupAs === "business" && (
                <Grid item xs={12} md={6}>
                  <Label text={t("cui")} htmlFor="user_cui_readonly" />
                  <InputField
                    id={"user_cui_readonly"}
                    readonly
                    value={inputValues.cui || "N/A"}
                  />
                </Grid>
              )}
              {userDetails?.bankDetails && (
                <Grid item xs={12} md={6}>
                  <Label
                    text={t("bankDetails")}
                    labelProps={{ fontSize: "1rem" }}
                  />
                  <Paper
                    elevation={0}
                    sx={{
                      padding: 2,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 2,
                      backgroundColor: "#f9f9f9",
                      border: "1px solid #ddd",
                    }}
                  >
                    <AccountBalanceIcon color="primary" fontSize="large" />

                    <Stack flex={1}>
                      <Typography fontWeight="bold">
                        {userDetails?.bankDetails?.accountHolderName}
                      </Typography>
                      <Typography color="text.secondary">
                        IBAN: {userDetails?.bankDetails?.iban}
                      </Typography>
                      <Typography color="text.secondary">
                        {t("bank")}:{" "}
                        {userDetails?.bankDetails?.bankName || "N/A"}
                      </Typography>
                    </Stack>

                    <IconButton
                      onClick={() => {
                        openAttachedAccountDialog.current.openDialog({
                          getDetails: fetchUserDetails,
                          type: "edit",
                          data: userDetails?.bankDetails,
                        });
                      }}
                      color="primary"
                      aria-label="edit bank details"
                    >
                      <EditIcon />
                    </IconButton>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </CustomContainer>

      {/* Dialogues */}
      <ChangePasswordDialog
        ref={openChangePassDialog}
        handleConfirm={handleChangePassword}
      />

      <SaveChangesDialog
        ref={openSaveChangesDialog}
        handleConfirm={handleUpdateUser}
      />

      <AttachBankDialog ref={openAttachedAccountDialog} />
    </DashboardHeader>
  );
};

export default UserSettingPage;
